/**
 * SoundsList - Polished playlist-style display for sound/audio search results
 * CC-BY license badge, smooth progress bar, refined row layout
 */

import { useRef, useState, useCallback, useEffect } from "react";

import { Download, Check, Play, Pause, Search, Music, Loader2 } from "lucide-react";

import { MediaLibraryResult } from "@/types/media-library-panel.types";
import { cn } from "@/lib/utils";
import {
  formatDuration,
  getProviderUrl,
  getAuthorUrl,
  getAttributionText,
  isAttributionRequired,
} from "./media-utils";
import { useInfiniteScroll } from "./use-infinite-scroll";
import { Skeleton } from "@/components/ui/skeleton";

interface SoundsListProps {
  results: MediaLibraryResult[];
  onDownload: (item: MediaLibraryResult) => void;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  downloadingIds?: Set<string>;
  isLoading?: boolean;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

function SoundSkeleton(): React.ReactElement {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5">
      <Skeleton className="flex-shrink-0 w-10 h-10 rounded-full" />
      <div className="flex-1 min-w-0 space-y-1.5">
        <Skeleton className="h-3.5 w-4/5" />
        <Skeleton className="h-2.5 w-2/5" />
      </div>
      <Skeleton className="w-8 h-8 rounded-full" />
    </div>
  );
}

function SoundsList({
  results,
  onDownload,
  selectedIds,
  onToggleSelect,
  downloadingIds,
  isLoading = false,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}: SoundsListProps): React.ReactElement | null {
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.ontimeupdate = null;
      audioRef.current = null;
    }
    setPlayingId(null);
    setCurrentTime(0);
    setAudioDuration(0);
  }, []);

  const handlePlayPause = useCallback(
    (e: React.MouseEvent, result: MediaLibraryResult) => {
      e.stopPropagation();
      const cardId = `${result.provider}-${result.id}`;

      if (playingId === cardId) {
        stopAudio();
        return;
      }

      stopAudio();

      const audio = new Audio(result.previewUrl);
      audio.ontimeupdate = () => setCurrentTime(audio.currentTime);
      audio.onloadedmetadata = () => setAudioDuration(audio.duration);
      audio.onended = () => stopAudio();
      audio.onerror = () => stopAudio();
      audio
        .play()
        .catch(() => stopAudio());
      audioRef.current = audio;
      setPlayingId(cardId);
      setAudioDuration(result.duration || 0);
    },
    [playingId, stopAudio],
  );

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  const sentinelRef = useInfiniteScroll({
    onLoadMore: onLoadMore || (() => {}),
    hasMore,
    isLoading: isLoadingMore,
    threshold: 500,
    debounceMs: 300,
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-0.5">
        {Array.from({ length: 8 }).map((_, i) => (
          <SoundSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-12 h-12 rounded-full bg-muted/60 flex items-center justify-center mb-3">
          <Music className="w-5 h-5 text-muted-foreground/50" />
        </div>
        <p className="text-sm font-medium text-muted-foreground">No results found</p>
        <p className="text-xs text-muted-foreground/60 mt-1">
          Try different keywords or change filters
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-0.5">
        {results.map((result) => {
          const cardId = `${result.provider}-${result.id}`;
          const isSelected = selectedIds?.has(cardId);
          const isDownloading = downloadingIds?.has(cardId);
          const isPlaying = playingId === cardId;
          const progress =
            isPlaying && audioDuration > 0
              ? (currentTime / audioDuration) * 100
              : 0;
          const attribution = getAttributionText(result.provider, result.author, "audio");
          const needsAttribution = isAttributionRequired(result.license);

          return (
            <div
              key={cardId}
              onClick={() => onToggleSelect?.(cardId)}
              className={cn(
                "group relative flex flex-col transition-all duration-200 cursor-pointer",
                "rounded-xl",
                isSelected && "bg-primary/10 border-l-2 border-primary",
                isPlaying && !isSelected && "bg-purple-500/5",
                !isSelected && !isPlaying && "hover:bg-muted/60",
              )}
            >
              {/* Main row */}
              <div className="flex items-center gap-3 px-3 py-2.5 min-w-0 overflow-hidden">
                {/* Play/Pause button */}
                <button
                  onClick={(e) => handlePlayPause(e, result)}
                  className={cn(
                    "flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200",
                    isPlaying
                      ? "bg-purple-500/15 text-purple-500"
                      : "bg-muted/80 text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                  aria-label={isPlaying ? "Pause" : "Play"}
                >
                  {isPlaying ? (
                    <Pause className="w-4 h-4" />
                  ) : (
                    <Play className="w-4 h-4 ml-0.5" />
                  )}
                </button>

                {/* Title + meta */}
                <div className="flex-1 min-w-0">
                  <p
                    className="text-[13px] font-medium leading-tight truncate max-w-[180px] sm:max-w-sm"
                    title={result.title}
                  >
                    {result.title}
                  </p>
                  <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                    <span className="text-[11px] text-muted-foreground truncate">
                      {result.author && result.authorUrl ? (
                        <a
                          href={getAuthorUrl(result.provider, result.authorUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="hover:text-foreground transition-colors"
                        >
                          {attribution}
                        </a>
                      ) : (
                        <a
                          href={getProviderUrl(result.provider)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="hover:text-foreground transition-colors"
                        >
                          {attribution}
                        </a>
                      )}
                    </span>
                    {result.duration != null && (
                      <>
                        <span className="text-[11px] text-muted-foreground/40">·</span>
                        <span
                          className={cn(
                            "text-[11px] tabular-nums flex-shrink-0 font-medium",
                            isPlaying ? "text-purple-500" : "text-muted-foreground/70",
                          )}
                        >
                          {isPlaying
                            ? `${formatDuration(currentTime)} / ${formatDuration(result.duration)}`
                            : formatDuration(result.duration)}
                        </span>
                      </>
                    )}
                    {needsAttribution && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-bold bg-amber-500/10 text-amber-600 dark:text-amber-400 leading-none tracking-wide">
                        CC BY
                      </span>
                    )}
                  </div>
                </div>

                {/* Download */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isDownloading) onDownload(result);
                  }}
                  disabled={isDownloading}
                  className={cn(
                    "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
                    "transition-all duration-200 z-10",
                    isDownloading
                      ? "text-primary opacity-100"
                      : "text-muted-foreground/60 opacity-0 group-hover:opacity-100 hover:text-foreground hover:bg-muted/80",
                  )}
                  aria-label="Download sound"
                >
                  {isDownloading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Download className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>

              {/* Progress bar */}
              {isPlaying && (
                <div className="h-[3px] bg-purple-500/10 mx-3 mb-2 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-purple-500 rounded-full transition-[width] duration-300 ease-linear"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}
            </div>
          );
        })}
        {isLoadingMore &&
          Array.from({ length: 4 }).map((_, i) => (
            <SoundSkeleton key={`loading-${i}`} />
          ))}
      </div>

      <div ref={sentinelRef} className="h-px" />
    </>
  );
}

export { SoundsList };
